import React, { ReactNode, useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { VehicleDetail } from '@1po/1po-bff-fe-spec/generated/common/vehicle/VehicleDetail';
import { Dispatch } from 'redux';
import { RootState } from 'app/AppStore';
import { CircleXMarkOutlined } from 'assets/icons';
import { SBodyFlex, SDiv, SHeaderFlex } from 'components/AddToEstimate/Popover/AddToEstimatePopover.styled';
import { useIsB2B } from 'components/B2BComponents/useIsB2B';
import QuantityModule from 'components/QuantityModule';
import { VehicleItem } from 'components/SearchVehicleCountrySelection/SearchVehicleCountrySelection';
import { setLastSearchedVehicleKey } from 'domains/catalog/Catalog.store';
import {
  createNewEstimate,
  getCurrentEstimateId,
  getEstimateSequenceNumber,
  getEstimateVehicle,
} from 'domains/estimate/Estimate.store';
import { CompactVehicleTabContentBase } from 'pages/CatalogPage/common';
import { theme } from 'styles';
import { BlackButton, Flex, Icon, PaddingBox, Radio, Text } from 'UI';
import { getCondArrayItem } from 'utils';

enum ADD_TO_ESTIMATE_OPTION {
  CURRENT,
  NEW,
  CURRENT_WITH_VEHICLE,
  NEW_WITH_VEHICLE,
}

interface RadioButtonsOptions {
  option: ADD_TO_ESTIMATE_OPTION;
  label: ReactNode;
  header: ReactNode;
  onConfirm?: () => void;
  vehicleDetail?: React.ReactNode;
}

const resolveAddToEstimateConditions = (
  vehicleDetail: VehicleDetail | undefined,
  openInCatalog: boolean,
  estimateWithoutVehicle: boolean,
  isEstimateActive: boolean,
  isB2B: boolean,
) => {
  const estimateHasNoVehicleAndIsNotActive = estimateWithoutVehicle && !isEstimateActive;
  const estimateWithVehicleAndNotInCatalog = !estimateWithoutVehicle && !openInCatalog;
  return {
    [ADD_TO_ESTIMATE_OPTION.CURRENT]: (estimateWithoutVehicle && isEstimateActive) || (isB2B && isEstimateActive),
    [ADD_TO_ESTIMATE_OPTION.CURRENT_WITH_VEHICLE]: !estimateWithoutVehicle && isEstimateActive,
    [ADD_TO_ESTIMATE_OPTION.NEW]: estimateHasNoVehicleAndIsNotActive || estimateWithVehicleAndNotInCatalog || isB2B,
    [ADD_TO_ESTIMATE_OPTION.NEW_WITH_VEHICLE]: vehicleDetail && !estimateWithoutVehicle && !isEstimateActive,
  };
};

const getRadioButtonsOptions = (
  dispatch: Dispatch,
  vehicleDetail: VehicleDetail | undefined,
  openInCatalog: boolean,
  estimateWithoutVehicle: boolean,
  isEstimateActive: boolean,
  isB2B: boolean,
): RadioButtonsOptions[] => {
  const resolvedConditions = resolveAddToEstimateConditions(
    vehicleDetail,
    openInCatalog,
    estimateWithoutVehicle,
    isEstimateActive,
    isB2B,
  );

  return [
    ...getCondArrayItem(
      resolvedConditions[ADD_TO_ESTIMATE_OPTION.CURRENT] && {
        option: ADD_TO_ESTIMATE_OPTION.CURRENT,
        header: (
          <Trans i18nKey={'estimate.add_dialog.add_to_current_estimate_short'}>Add reference to current estimate</Trans>
        ),
        label: (
          <Trans i18nKey={'estimate.add_dialog.add_to_current_estimate'}>
            Add to <strong>current</strong> estimate not linked to the current vehicle
          </Trans>
        ),
      },
    ),
    ...getCondArrayItem(
      resolvedConditions[ADD_TO_ESTIMATE_OPTION.CURRENT_WITH_VEHICLE] && {
        option: ADD_TO_ESTIMATE_OPTION.CURRENT_WITH_VEHICLE,
        header: (
          <Trans i18nKey={'estimate.add_dialog.add_to_current_estimate_short'}>Add reference to current estimate</Trans>
        ),
        label: (
          <Trans i18nKey={'estimate.add_dialog.add_to_current_estimate_with_vehicle2'}>
            Add to the <strong>current</strong> estimate from the current car
          </Trans>
        ),
        vehicleDetail: vehicleDetail ? <VehicleItem v={vehicleDetail} /> : null,
      },
    ),
    ...getCondArrayItem(
      resolvedConditions[ADD_TO_ESTIMATE_OPTION.NEW] && {
        option: ADD_TO_ESTIMATE_OPTION.NEW,
        header: <Trans i18nKey={'estimate.add_dialog.add_to_new_estimate_short'}>Add reference to new estimate</Trans>,
        label: (
          <Trans i18nKey={'estimate.add_dialog.add_to_new_estimate'}>
            Add to a <strong>new</strong> estimate not linked to the current vehicle
          </Trans>
        ),
        onConfirm: () => {
          dispatch(createNewEstimate({ vehicle: undefined }));
          dispatch(setLastSearchedVehicleKey(undefined));
        },
      },
    ),
    ...getCondArrayItem(
      resolvedConditions[ADD_TO_ESTIMATE_OPTION.NEW_WITH_VEHICLE] && {
        option: ADD_TO_ESTIMATE_OPTION.NEW_WITH_VEHICLE,
        header: <Trans i18nKey={'estimate.add_dialog.add_to_new_estimate_short'}>Add reference to new estimate</Trans>,
        label: (
          <Trans i18nKey={'estimate.add_dialog.add_to_new_estimate_with_vehicle'}>
            Add to a <strong>new</strong> estimate from the current car
          </Trans>
        ),
        onConfirm: () => {
          dispatch(createNewEstimate({ vehicle: vehicleDetail }));
        },
        vehicleDetail: vehicleDetail ? <VehicleItem v={vehicleDetail} /> : null,
      },
    ),
  ].filter((v) => !!v) as RadioButtonsOptions[];
};

const AddToEstimatePopover = ({
  handleClose,
  handleAddToEstimateClick,
  vehicleDetail,
  openInCatalog = false,
  initCount = 1,
  sellerNumberHandler,
  sellerNameHandler,
}: Readonly<{
  handleClose: () => void;
  handleAddToEstimateClick: (quantity?: number, sellerNumberHandler?: string, sellerNameHandler?: string) => void;
  vehicleDetail: VehicleDetail | undefined;
  openInCatalog: boolean;
  initCount: number;
  sellerNumberHandler?: string;
  sellerNameHandler?: string;
}>) => {
  const { t } = useTranslation();
  const [count, setCount] = useState<number>(initCount ?? 1);
  const isB2B = useIsB2B();
  const dispatch = useDispatch();
  const estimateId = useSelector(getCurrentEstimateId);
  const estimateVehicle = useSelector((state: RootState) => getEstimateVehicle(state, estimateId));
  const estimateVehicleKey = estimateVehicle?.vehicleKey;
  const isEstimateActive = useSelector((state: RootState) => getEstimateSequenceNumber(state, estimateId));

  useEffect(() => {
    setCount(initCount);
  }, [initCount]);

  const options = useMemo(() => {
    return getRadioButtonsOptions(
      dispatch,
      vehicleDetail,
      openInCatalog,
      estimateVehicleKey === undefined,
      !!isEstimateActive,
      isB2B,
    );
  }, [dispatch, estimateVehicleKey, isB2B, openInCatalog, vehicleDetail, isEstimateActive]);
  const [addToEstimateOption, setAddToEstimateOption] = useState<ADD_TO_ESTIMATE_OPTION>(options[0].option);

  const handleConfirm = () => {
    const option = options.find((o) => o.option === addToEstimateOption);
    option?.onConfirm?.();
    handleClose();
    handleAddToEstimateClick(count, sellerNumberHandler, sellerNameHandler);
  };

  const singleOption: RadioButtonsOptions | boolean = options?.length === 1 ? options[0] : false;

  return (
    <SDiv>
      <Flex direction={'column'} data-cy={'add-to-estimate-popover'}>
        <SHeaderFlex direction={'row'} background={theme.color.clear_blue} minHeight={60} align={'center'}>
          <Flex alignSelf={'center'} minHeight={20}>
            <Text type="heading4" color="white" data-cy={'add-to-estimate-text'}>
              {singleOption ? singleOption.header : t('estimate.add_dialog.title', 'Add reference to estimate')}
            </Text>
          </Flex>
          <Icon
            IconComponent={CircleXMarkOutlined}
            round
            size={24}
            color={theme.color.white}
            onClick={handleClose}
            data-cy={'add-to-estimate-popover-cross'}
          />
        </SHeaderFlex>
        {vehicleDetail && !isB2B && (
          <CompactVehicleTabContentBase result={vehicleDetail} vehicleImage={vehicleDetail.imageUrl} isBMM={false} />
        )}
        <SBodyFlex direction={'column'} gap={10}>
          <Text type="base" bold>
            {singleOption
              ? t('estimate.add_dialog.select_quantity', 'Select quantity')
              : t('estimate.add_dialog.make_your_selection', 'Make your selection')}
          </Text>
          <Flex direction={'column'} justify={'flex-start'}>
            {singleOption ? (
              <Text type="base" data-cy={'add-to-estimate-popover-text'}>
                {singleOption.label}
              </Text>
            ) : (
              options.map((o) => (
                <React.Fragment key={o.option}>
                  <Radio
                    checked={addToEstimateOption === o.option}
                    label={o.label}
                    onChange={() => setAddToEstimateOption(o.option)}
                  />
                  <PaddingBox pt={5} />
                </React.Fragment>
              ))
            )}
          </Flex>
          <QuantityModule value={count} onChange={setCount} size={'large'} noFixedWidth />
          <BlackButton
            onClick={handleConfirm}
            shape="semiRound"
            size={'xlarge'}
            data-cy={'add-to-estimate-popover-add-button'}
          >
            <Trans i18nKey={'common.action.add'}>{'Add'}</Trans>
          </BlackButton>
        </SBodyFlex>
      </Flex>
    </SDiv>
  );
};

export default AddToEstimatePopover;
